/**
 * Панель задачи. Макет data/Задачник.dc.html, секции 1417–1900.
 *
 * Семь секций в порядке макета: заголовок, описание, файлы, связанные задачи,
 * основные параметры, комментарии, история.
 *
 * §3.4: панель открывается поверх доски, но URL отражает открытую задачу —
 * по прямой ссылке восстанавливается и доска, и карточка. Закрытие
 * возвращает на доску, а не в корень.
 *
 * §11.8: редактирование посылает version; расхождение даёт 409, и это
 * содержательный конфликт, а не сбой — пользователю показывается, что
 * задачу изменили параллельно.
 */

import { errorMessage, usePermission, useTopics, type WsError } from "@taskflow/client-core";
import { copyText } from "@/shared/lib/clipboard";
import { Permission, ProjectBindingMode } from "@taskflow/contracts";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useMidnightRefresh } from "@/shared/api/useMidnightRefresh";
import {
  Checkbox,
  ConfirmDialog,
  ErrorState,
  IconButton,
  SkeletonRows,
  SlideOver,
  toast,
} from "@/shared/ui";
import { Archive, Copy, Link2, Pencil, Trash2 } from "@/shared/ui/motionIcons";

import { useBoardQuery } from "@taskflow/client-core/boards";
import { ViewMarkPalette } from "../boards/ViewMarkPalette";
import { reportTaskSection } from "./reportTaskPath";
import { useUiPrefsQuery } from "@taskflow/client-core/profile";
import {
  useArchiveTaskMutation,
  useDeleteTaskMutation,
  useSetTaskViewedMutation,
  useTaskQuery,
} from "@taskflow/client-core/tasks";
import { TaskSectionsProvider } from "./sections/collapse";
import { TASK_SECTIONS } from "./sections/taskSections";
import { TaskAttachments } from "./sections/TaskAttachments";
import { TaskComments } from "./sections/TaskComments";
import { TaskDescription } from "./sections/TaskDescription";
import { TaskHistory } from "./sections/TaskHistory";
import { TaskLinks } from "./sections/TaskLinks";
import { TaskParameters } from "./sections/TaskParameters";
import { TaskTitleEditor } from "./sections/TaskTitle";

export function TaskPanel({
  taskKey: givenKey,
  onClose,
}: {
  /** Задача не из маршрута — например, открытая поверх архива доски. */
  taskKey?: string;
  onClose?: () => void;
} = {}) {
  const { boardId: routeBoardId = "", taskKey: routeKey = "" } = useParams<{
    boardId: string;
    taskKey: string;
  }>();
  const taskKey = givenKey ?? routeKey;
  const navigate = useNavigate();
  const location = useLocation();
  const task = useTaskQuery(taskKey, { skip: taskKey === "" });

  // §3.5: по прямой ссылке сначала загружается задача, и только потом
  // её доска. Из отчётности boardId в адресе нет вовсе — брать его
  // из открытой доски нельзя, иначе задача ОАП откроется под доской ОА.
  const boardId = routeBoardId === "" ? (task.data?.boardId ?? "") : routeBoardId;

  const board = useBoardQuery(boardId, { skip: boardId === "" });
  // Комментарии, файлы и связи других участников — без перезагрузки.
  // Перенос в другой столбец сервер сообщает только подписчикам доски
  // (task.moved на board:{id}): из отчёта панель открыта не поверх доски.
  useTopics([taskKey === "" ? null : `task:${taskKey}`, boardId === "" ? null : `board:${boardId}`]);
  // Какие блоки карточки развёрнуты при открытии — личная настройка (§5).
  // Пока ответ не пришёл, развёрнуто всё: показать карточку свёрнутой и
  // тут же развернуть её было бы хуже, чем наоборот.
  const uiPrefs = useUiPrefsQuery();
  const expandedSections = uiPrefs.data?.expandedTaskSections ?? TASK_SECTIONS;
  const boardPermissions = board.data?.permissions ?? [];

  const canEdit = usePermission(
    [Permission.TASK_EDIT_OWN, Permission.TASK_EDIT_ALL],
    boardPermissions,
  );
  // Название, описание, приоритет, тип, срок и статус правит любой
  // участник доски с правом редактирования — так же решает сервер.
  const [editingTitle, setEditingTitle] = useState(false);
  const canComment = usePermission(Permission.COMMENT_CREATE, boardPermissions);
  const canManageLinks = usePermission(Permission.LINK_MANAGE, boardPermissions);
  const canAddFile = usePermission(Permission.FILE_ADD, boardPermissions);
  const canArchive = usePermission(Permission.TASK_ARCHIVE, boardPermissions);
  const canDelete = usePermission(Permission.TASK_DELETE, boardPermissions);

  // §25.6: архивация из карточки. Мутация существовала, но ни один экран
  // её не вызывал — задачу можно было восстановить, а отправить в архив
  // было нечем.
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiveTask, { isLoading: archiveLoading }] = useArchiveTaskMutation();

  const confirmArchive = async (reason: string | null) => {
    if (task.data === undefined) return;
    setArchiveError(null);
    try {
      await archiveTask({
        key: task.data.key,
        version: task.data.version,
        reason: reason ?? "",
      }).unwrap();
      setArchiving(false);
      toast.success("Задача отправлена в архив", `${task.data.key} доступна в архиве доски`);
      void navigate({ pathname: `/boards/${boardId}`, search: location.search });
    } catch (raw) {
      // Отказ показывается в диалоге, а не тостом: причина уже введена,
      // и закрывать форму вместе с ней было бы наказанием за чужую ошибку.
      setArchiveError(errorMessage(raw));
    }
  };

  // Несохранённое описание не теряется молча: закрытие панели спрашивает.
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const requestClose = () => {
    if (descriptionDirty) setConfirmClose(true);
    else close();
  };

  // Закрытие в два шага: панель уезжает за край экрана, и только потом
  // меняется маршрут — сразу после навигации панели уже нет в дереве.
  const [open, setOpen] = useState(true);
  const close = () => setOpen(false);

  // Удаление навсегда — и активной задачи, и архивной: вернуть её нельзя,
  // поэтому подтверждение называет, что пропадёт вместе с ней.
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteTask, { isLoading: deleteLoading }] = useDeleteTaskMutation();

  const confirmDelete = async () => {
    if (task.data === undefined) return;
    setDeleteError(null);
    try {
      await deleteTask({ key: task.data.key, version: task.data.version }).unwrap();
      setDeleting(false);
      toast.success("Задача удалена", `${task.data.key} удалена навсегда`);
      // Карточки больше нет — панель закрывается туда, откуда её открыли.
      setDescriptionDirty(false);
      close();
    } catch (raw) {
      setDeleteError(errorMessage(raw));
    }
  };

  // Закрытие возвращает туда, откуда пришли: из отчёта — в тот же раздел
  // отчёта (§25.7), с доски — на доску.
  const leave = () => {
    if (onClose !== undefined) {
      onClose();
      return;
    }
    const reportSection = reportTaskSection(location.pathname);
    // Параметры адреса сохраняются: прежде закрытие карточки сбрасывало
    // фильтры доски и период отчёта (§25.5).
    void navigate({
      pathname: reportSection ?? `/boards/${boardId}`,
      search: location.search,
    });
  };

  // Отметка о просмотре — только на доске «проект для каждого столбца»
  // с включённым режимом: в другом месте её результат нигде не виден.
  // Отметка личная: ставит её каждый, кто видит задачу, — и только для себя.
  const [setViewed, { isLoading: viewedSaving }] = useSetTaskViewedMutation();
  const viewMarkOn =
    board.data?.projectBindingMode === ProjectBindingMode.COLUMN &&
    board.data.viewMarkEnabled;
  // Отметка гаснет в 00:00 — открытая с вечера карточка узнаёт об этом сама.
  useMidnightRefresh(viewMarkOn === true);
  const toggleViewed = async (viewed: boolean) => {
    if (task.data === undefined) return;
    try {
      await setViewed({ key: task.data.key, viewed }).unwrap();
    } catch (raw) {
      toast.error(raw as WsError, "Не удалось изменить отметку о просмотре");
    }
  };

  const copy = async (value: string, what: string) => {
    try {
      await copyText(value);
      toast.success(`${what} скопирован`);
    } catch {
      // Браузер отказал и в буфере обмена, и в команде копирования — не молчим.
      toast.warning("Не удалось скопировать", "Браузер запретил доступ к буферу обмена");
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={requestClose}
      afterLeave={leave}
      width="xl"
      title={
        task.data === undefined ? (
          taskKey
        ) : (
          // Длинное название показывается целиком: прежде оно обрезалось
          // многоточием, и в детализации задачи прочитать его было негде.
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-[13px] font-semibold text-faint">{task.data.key}</span>
            <span className="min-w-0 wrap-anywhere">{task.data.title}</span>
          </span>
        )
      }
      actions={
        <>
          {viewMarkOn && task.data !== undefined && !task.data.archived && (
            <span
              className="mr-1 flex items-center"
              title={
                task.data.viewed
                  ? `Вы отметили задачу в ${task.data.viewedAtLabel ?? ""}. Отметка видна только вам и снимется в 00:00`
                  : "Отметить задачу просмотренной — отметка видна только вам и снимется в 00:00"
              }
            >
              <Checkbox
                label="Просмотрено"
                checked={task.data.viewed}
                disabled={viewedSaving}
                onChange={(checked) => void toggleViewed(checked)}
              />
            </span>
          )}
          {/* Цвет отметки личный — выбрать его может каждый участник,
              а не только тот, кому доступны настройки доски. */}
          {viewMarkOn && board.data !== undefined && task.data !== undefined && !task.data.archived && (
            // Палитра прямо в шапке, без всплывающего окна: системное окно
            // выбора цвета уводит фокус, и всплывающее закрывалось бы.
            <span className="mr-1 inline-flex">
              <ViewMarkPalette
                boardId={board.data.id}
                value={board.data.viewMarkColor}
                size="sm"
                label="Мой цвет отметки о просмотре"
                title="Мой цвет отметки — виден только вам"
              />
            </span>
          )}
          {canEdit && task.data !== undefined && !task.data.archived && !editingTitle && (
            <IconButton
              icon={Pencil}
              label="Изменить название"
              size="sm"
              onClick={() => setEditingTitle(true)}
            />
          )}
          {canArchive && task.data !== undefined && !task.data.archived && (
            <IconButton
              icon={Archive}
              label="В архив"
              size="sm"
              onClick={() => {
                setArchiveError(null);
                setArchiving(true);
              }}
            />
          )}
          {canDelete && task.data !== undefined && (
            <IconButton
              icon={Trash2}
              label="Удалить задачу"
              size="sm"
              variant="danger"
              onClick={() => {
                setDeleteError(null);
                setDeleting(true);
              }}
            />
          )}
          {/* §31: копирование номера и URL задачи — отдельное требование. */}
          <IconButton
            icon={Copy}
            label="Скопировать номер задачи"
            size="sm"
            onClick={() => void copy(taskKey, "Номер задачи")}
          />
          <IconButton
            icon={Link2}
            label="Скопировать ссылку на задачу"
            size="sm"
            onClick={() => void copy(window.location.href, "Адрес задачи")}
          />
        </>
      }
    >
      {task.isLoading && <SkeletonRows rows={6} className="p-5" />}

      {task.error !== undefined && (
        <ErrorState
          title="Задача недоступна"
          message={(task.error as WsError).data.message}
          correlationId={(task.error as WsError).data.correlationId}
          onRetry={() => void task.refetch()}
        />
      )}

      {task.data !== undefined && (
        <div className="flex flex-col divide-y divide-line">
          {task.data.archived && (
            <p className="bg-warning-soft px-5 py-2 text-[13px] font-semibold text-warning">
              Задача в архиве. Изменения недоступны до восстановления.
            </p>
          )}

          {editingTitle && !task.data.archived && (
            <TaskTitleEditor task={task.data} onDone={() => setEditingTitle(false)} />
          )}
          <TaskDescription
            task={task.data}
            canEdit={canEdit && !task.data.archived}
            canAttach={canAddFile && !task.data.archived}
            onDirtyChange={setDescriptionDirty}
          />
          <TaskSectionsProvider expanded={expandedSections}>
            <TaskAttachments task={task.data} canAdd={canAddFile && !task.data.archived} />
            <TaskLinks
              taskKey={taskKey}
              departmentId={board.data?.departmentId ?? null}
              canManage={canManageLinks && !task.data.archived}
            />
            <TaskParameters
              task={task.data}
              boardId={boardId}
              board={board.data}
              statusSource={board.data?.statusSource ?? null}
              canEdit={canEdit && !task.data.archived}
              boardPermissions={boardPermissions}
            />
            <TaskComments
              key={taskKey}
              taskKey={taskKey}
              canComment={canComment && !task.data.archived}
              canAttach={canAddFile && !task.data.archived}
            />
            <TaskHistory taskKey={taskKey} />
          </TaskSectionsProvider>
        </div>
      )}

        {/* Внутри панели — так же, как подтверждения в разделах связей,
          файлов и комментариев. Тексты — из макета. Причина обязательна
          по §15.3 и попадает в аудит (§23.3); в макете у незавершённой
          задачи она помечена «необязательно» — расхождение вынесено
          в отчёт приёмки. */}
      <ConfirmDialog
        open={confirmClose}
        title="Закрыть без сохранения?"
        description="В описании есть несохранённые изменения. Если закрыть задачу, они будут потеряны."
        confirmLabel="Закрыть без сохранения"
        tone="danger"
        onConfirm={() => {
          setConfirmClose(false);
          setDescriptionDirty(false);
          close();
        }}
        onCancel={() => setConfirmClose(false)}
      />

      <ConfirmDialog
        open={archiving}
        title="Отправить задачу в архив?"
        description="Задача исчезнет с активной доски, но её данные, файлы, комментарии, связи и история сохранятся."
        confirmLabel="В архив"
        reasonField={{ label: "Причина архивации", required: true }}
        loading={archiveLoading}
        error={archiveError}
        onConfirm={(reason) => void confirmArchive(reason)}
        onCancel={() => setArchiving(false)}
      />

      <ConfirmDialog
        open={deleting}
        title="Удалить задачу навсегда?"
        description={`${task.data?.key ?? taskKey} «${task.data?.title ?? ""}» будет удалена из системы вместе с комментариями, файлами, связями и историей.`}
        warning="Восстановить удалённую задачу нельзя. Если она может понадобиться — отправьте её в архив."
        confirmLabel="Удалить навсегда"
        tone="danger"
        loading={deleteLoading}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(false)}
      />
    </SlideOver>
  );
}
